<script setup lang="ts">
import { ref, nextTick, watch } from 'vue'
import { aiService } from '@/api/aiService'
import { useNotificationStore } from '@/stores/notification'

interface ChatMessage {
  id: string
  sender: 'user' | 'ai'
  text: string
  time: string
  quickTopics?: string[]
  fileName?: string
}

const isOpen = ref(false)
const notificationStore = useNotificationStore()
const selectedLang = ref<'sw' | 'en'>('sw')
const userInput = ref('')
const loading = ref(false)
const isListening = ref(false)
const isSpeaking = ref(false)
const chatContainer = ref<HTMLDivElement | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)

const attachedFile = ref<{ file?: File; base64?: string; name: string; type: string } | null>(null)

let recognition: any = null

const initialSwGreeting: ChatMessage = {
  id: 'init-sw',
  sender: 'ai',
  text: 'Habari! 👋 Mimi ni **Mtupori Assistant**. Naweza kukusaidia kwa sauti, maandishi, au **picha/faili za screenshot** kuchanganua hali ya shamba, mifugo, mauzo ya duka, matumizi au kukusaidia unapokumbana na tatizo au kosa.',
  time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  quickTopics: ['Hali ya Shamba sasa', 'Stock na Mauzo', 'Matumizi na Mafuta', 'Wafanyakazi & Kazi']
}

const initialEnGreeting: ChatMessage = {
  id: 'init-en',
  sender: 'ai',
  text: 'Hello! 👋 I am **Mtupori Assistant**. I can assist you via voice, text, or **picture/file uploads** to analyze farm stats, shop sales, finances, or troubleshoot errors.',
  time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  quickTopics: ['Current Farm Status', 'Stock & Sales', 'Expenses & Fuel', 'Workforce & Tasks']
}

const messages = ref<ChatMessage[]>([initialSwGreeting])

watch(selectedLang, (newLang) => {
  if (messages.value.length <= 1) {
    messages.value = [newLang === 'sw' ? initialSwGreeting : initialEnGreeting]
  }
})

const scrollToBottom = async () => {
  await nextTick()
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight
  }
}

// Handle File / Picture Upload Selection
const triggerFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click()
  }
}

const handleFileUpload = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (!target.files || target.files.length === 0) return
  const file = target.files[0]

  const reader = new FileReader()
  reader.onload = (event) => {
    const base64Str = event.target?.result as string
    attachedFile.value = {
      file,
      base64: base64Str,
      name: file.name,
      type: file.type.startsWith('image/') ? 'image' : 'file'
    }
  }
  reader.readAsDataURL(file)
  target.value = ''
}

const removeAttachedFile = () => {
  attachedFile.value = null
}

// Text to Speech (TTS)
const speakMessage = (text: string) => {
  if (!('speechSynthesis' in window)) return

  if (isSpeaking.value) {
    window.speechSynthesis.cancel()
    isSpeaking.value = false
    return
  }

  const cleanText = text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/###?/g, '')
    .replace(/\|/g, ' ')
    .replace(/<[^>]*>?/gm, '')
    .trim()

  const utterance = new SpeechSynthesisUtterance(cleanText)
  utterance.lang = selectedLang.value === 'sw' ? 'sw-TZ' : 'en-US'
  utterance.rate = 1.0

  utterance.onstart = () => { isSpeaking.value = true }
  utterance.onend = () => { isSpeaking.value = false }
  utterance.onerror = () => { isSpeaking.value = false }

  window.speechSynthesis.speak(utterance)
}

// Speech to Text (Voice Command)
const toggleVoiceRecognition = () => {
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition

  if (!SpeechRecognition) {
    notificationStore.showWarning(selectedLang.value === 'sw' 
      ? 'Kivinjari chako hakiungi mkono Sauti (Web Speech API).' 
      : 'Your browser does not support Web Speech Recognition.')
    return
  }

  if (isListening.value) {
    if (recognition) recognition.stop()
    isListening.value = false
    return
  }

  try {
    recognition = new SpeechRecognition()
    recognition.lang = selectedLang.value === 'sw' ? 'sw-TZ' : 'en-US'
    recognition.continuous = false
    recognition.interimResults = true

    recognition.onstart = () => {
      isListening.value = true
    }

    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((result: any) => result[0].transcript)
        .join('')

      userInput.value = transcript
    }

    recognition.onend = () => {
      isListening.value = false
      if (userInput.value.trim() || attachedFile.value) {
        sendMessage()
      }
    }

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error)
      isListening.value = false
    }

    recognition.start()
  } catch (e) {
    console.error(e)
    isListening.value = false
  }
}

const sendMessage = async (textToSend?: string) => {
  const text = (textToSend || userInput.value).trim()
  const currentAttachment = attachedFile.value

  if ((!text && !currentAttachment) || loading.value) return

  const messageText = text || (currentAttachment ? (selectedLang.value === 'sw' ? `Tafadhali changanua faili/picha hii: ${currentAttachment.name}` : `Please analyze this attached file: ${currentAttachment.name}`) : '')

  const userMsg: ChatMessage = {
    id: String(Date.now()),
    sender: 'user',
    text: messageText,
    fileName: currentAttachment?.name,
    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  messages.value.push(userMsg)
  if (!textToSend) userInput.value = ''
  attachedFile.value = null
  scrollToBottom()

  // Assemble rolling multi-turn conversation history (excluding greeting and current message)
  const history = messages.value
    .slice(0, -1)
    .filter(m => m.id !== 'init-sw' && m.id !== 'init-en' && m.text.trim())
    .slice(-10)
    .map(m => ({
      role: (m.sender === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
      content: m.text
    }))

  loading.value = true
  try {
    const attachmentPayload = currentAttachment ? {
      file: currentAttachment.file,
      image_base64: currentAttachment.base64,
      filename: currentAttachment.name
    } : undefined

    const res = await aiService.sendCopilotMessage(
      messageText,
      selectedLang.value,
      attachmentPayload,
      history
    )

    const aiMsg: ChatMessage = {
      id: String(Date.now() + 1),
      sender: 'ai',
      text: res.message,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      quickTopics: res.quick_topics
    }
    messages.value.push(aiMsg)
    if (res.lang) selectedLang.value = res.lang
  } catch (err) {
    console.error(err)
    messages.value.push({
      id: String(Date.now() + 2),
      sender: 'ai',
      text: selectedLang.value === 'sw' 
        ? 'Samahani, kumetokea hitilafu ya muunganisho. Tafadhali jaribu tena baada ya muda mfupi.'
        : 'Sorry, a temporary network error occurred. Please try again shortly.',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    })
  } finally {
    loading.value = false
    scrollToBottom()
  }
}

// Markdown and HTML table formatter for Mtupori Assistant responses
const formatText = (text: string) => {
  if (!text) return ''
  let formatted = text

  if (formatted.includes('|')) {
    const lines = formatted.split('\n')
    let inTable = false
    let tableHtml = '<div class="overflow-x-auto my-2"><table class="w-full text-xs border-collapse border border-slate-700 bg-slate-800/90 rounded-xl overflow-hidden">'
    const resultLines: string[] = []

    for (let line of lines) {
      const trimmed = line.trim()
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        if (trimmed.includes('---')) continue

        const cells = trimmed.split('|').slice(1, -1).map(c => c.trim())
        if (!inTable) {
          inTable = true
          tableHtml += '<thead><tr class="bg-indigo-950 text-indigo-200 border-b border-slate-700">'
          cells.forEach(c => { tableHtml += `<th class="p-2 text-left font-bold border-r border-slate-700/50">${c}</th>` })
          tableHtml += '</tr></thead><tbody>'
        } else {
          tableHtml += '<tr class="border-b border-slate-700/50 hover:bg-slate-700/40">'
          cells.forEach(c => { tableHtml += `<td class="p-2 border-r border-slate-700/50 text-slate-200">${c}</td>` })
          tableHtml += '</tr>'
        }
      } else {
        if (inTable) {
          inTable = false
          tableHtml += '</tbody></table></div>'
          resultLines.push(tableHtml)
          tableHtml = '<div class="overflow-x-auto my-2"><table class="w-full text-xs border-collapse border border-slate-700 bg-slate-800/90 rounded-xl overflow-hidden">'
        }
        resultLines.push(line)
      }
    }
    if (inTable) {
      tableHtml += '</tbody></table></div>'
      resultLines.push(tableHtml)
    }
    formatted = resultLines.join('\n')
  }

  formatted = formatted
    .replace(/^### (.*$)/gim, '<h3 class="text-xs font-bold text-indigo-300 mt-2 mb-1">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="text-sm font-bold text-amber-400 mt-3 mb-1">$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-bold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="text-slate-300">$1</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-slate-800 text-indigo-300 px-1 py-0.5 rounded text-xs font-mono">$1</code>')
    .replace(/\n/g, '<br/>')

  return formatted
}
</script>

<template>
  <div class="fixed bottom-6 right-6 z-50 font-sans">
    <!-- Floating Trigger Button -->
    <button
      v-if="!isOpen"
      @click="isOpen = true"
      class="group relative flex items-center justify-center p-4 bg-gradient-to-tr from-indigo-600 via-purple-600 to-amber-500 rounded-2xl shadow-2xl hover:scale-105 transition-all duration-300 border border-white/20"
      aria-label="Open Mtupori Assistant"
    >
      <div class="absolute -inset-1 bg-gradient-to-r from-amber-400 to-indigo-500 rounded-2xl blur-md opacity-40 group-hover:opacity-75 transition duration-300"></div>

      <div class="relative flex items-center gap-2.5 text-white font-bold text-sm">
        <span class="text-xl">🤖</span>
        <span class="hidden sm:inline tracking-wide">Mtupori Assistant</span>
        <span class="flex h-2 w-2 relative">
          <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
          <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
        </span>
      </div>
    </button>

    <!-- Chat Modal Window -->
    <div
      v-else
      class="w-[92vw] sm:w-[420px] h-[590px] bg-slate-900/95 border border-indigo-500/30 rounded-3xl shadow-2xl flex flex-col overflow-hidden backdrop-blur-xl animate-fadeIn"
    >
      <!-- Header -->
      <div class="p-4 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border-b border-slate-800 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-500 to-amber-500 flex items-center justify-center text-white text-lg shadow-md">
            🤖
          </div>
          <div>
            <h3 class="text-sm font-bold text-white tracking-wide">Mtupori Assistant</h3>
            <p class="text-[10px] text-emerald-400 flex items-center gap-1 font-medium">
              <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              {{ selectedLang === 'sw' ? 'Msaidizi wa Sauti, Data & Picha' : 'Voice, Data & Image Diagnostics' }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- Language Toggle -->
          <div class="flex items-center bg-slate-800 p-0.5 rounded-lg border border-slate-700">
            <button
              @click="selectedLang = 'sw'"
              class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-colors"
              :class="selectedLang === 'sw' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'"
            >
              SW
            </button>
            <button
              @click="selectedLang = 'en'"
              class="px-2 py-0.5 text-[10px] font-bold rounded-md transition-colors"
              :class="selectedLang === 'en' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'"
            >
              EN
            </button>
          </div>

          <!-- Close Button -->
          <button
            @click="isOpen = false"
            class="w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
          >
            ✕
          </button>
        </div>
      </div>

      <!-- Scrollable Chat Messages Area -->
      <div ref="chatContainer" class="flex-1 p-4 overflow-y-auto space-y-3 custom-scrollbar text-xs">
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="flex flex-col"
          :class="msg.sender === 'user' ? 'items-end' : 'items-start'"
        >
          <div
            class="relative max-w-[85%] px-3.5 py-2.5 rounded-2xl leading-relaxed font-normal shadow-sm group"
            :class="
              msg.sender === 'user'
                ? 'bg-indigo-600 text-white rounded-br-none'
                : 'bg-slate-800/90 text-slate-200 border border-slate-700/80 rounded-bl-none'
            "
          >
            <!-- User Attached File Tag -->
            <div v-if="msg.fileName" class="mb-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 bg-indigo-900/60 border border-indigo-400/30 rounded-lg text-[10px] text-indigo-200">
              <span>📸</span>
              <span class="truncate max-w-[140px]">{{ msg.fileName }}</span>
            </div>

            <div v-html="formatText(msg.text)"></div>
            
            <div class="flex items-center justify-between mt-1 text-[9px]">
              <span :class="msg.sender === 'user' ? 'text-indigo-100' : 'text-slate-400'">{{ msg.time }}</span>
              
              <!-- Speak Response Button for AI Messages -->
              <button
                v-if="msg.sender === 'ai'"
                @click="speakMessage(msg.text)"
                class="ml-2 text-indigo-300 hover:text-white transition-colors"
                title="Soma kwa sauti (Speak response)"
              >
                🔊
              </button>
            </div>
          </div>

          <!-- Quick Action Topic Chips -->
          <div
            v-if="msg.quickTopics && msg.quickTopics.length > 0"
            class="flex flex-wrap gap-1.5 mt-2 max-w-[90%]"
          >
            <button
              v-for="topic in msg.quickTopics"
              :key="topic"
              @click="sendMessage(topic)"
              class="px-2.5 py-1 rounded-full text-[10px] font-semibold bg-indigo-950/60 hover:bg-indigo-600/50 text-indigo-300 hover:text-white border border-indigo-500/30 transition-all text-left"
            >
              ⚡ {{ topic }}
            </button>
          </div>
        </div>

        <!-- Thinking Indicator -->
        <div v-if="loading" class="flex items-start gap-2">
          <div class="px-3.5 py-2.5 bg-slate-800 text-slate-300 rounded-2xl rounded-bl-none border border-slate-700 flex items-center gap-1.5">
            <span class="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce"></span>
            <span class="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce delay-100"></span>
            <span class="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce delay-200"></span>
            <span class="text-[11px] font-medium text-slate-400 ml-1">Mtupori Assistant analyzing request...</span>
          </div>
        </div>
      </div>

      <!-- File Preview Chip (If attached) -->
      <div v-if="attachedFile" class="px-3 py-1.5 bg-indigo-950/80 border-t border-indigo-800/50 flex items-center justify-between text-xs">
        <div class="flex items-center gap-2 text-indigo-200 truncate">
          <span class="text-sm">📸</span>
          <span class="truncate font-medium text-[11px]">{{ attachedFile.name }}</span>
        </div>
        <button
          @click="removeAttachedFile"
          class="text-slate-400 hover:text-rose-400 text-xs px-1 font-bold"
          title="Ondoa faili"
        >
          ✕
        </button>
      </div>

      <!-- Hidden Input File -->
      <input
        type="file"
        ref="fileInputRef"
        @change="handleFileUpload"
        accept="image/*,.pdf,.txt,.csv,.json,.log"
        class="hidden"
      />

      <!-- Input Bar with Voice (Sauti) Mic & Image/File Attachment Button -->
      <div class="p-3 bg-slate-900 border-t border-slate-800">
        <form @submit.prevent="sendMessage()" class="flex items-center gap-1.5">
          <!-- Voice Mic Button -->
          <button
            type="button"
            @click="toggleVoiceRecognition"
            class="p-2 rounded-xl transition-all border shadow-sm flex items-center justify-center"
            :class="
              isListening
                ? 'bg-rose-600 text-white border-rose-500 animate-pulse'
                : 'bg-slate-800 text-slate-300 hover:text-white border-slate-700 hover:bg-slate-700'
            "
            :title="isListening ? 'Inasikiliza sauti yako...' : 'Bofya hapa kuongea kwa sauti (Voice Input)'"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>

          <!-- File / Image Upload Button -->
          <button
            type="button"
            @click="triggerFileInput"
            class="p-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white border border-slate-700 hover:bg-slate-700 transition-all shadow-sm flex items-center justify-center"
            title="Weka picha ya kosa au faili (Upload Image/Screenshot or Document)"
          >
            <svg class="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>

          <input
            v-model="userInput"
            type="text"
            :placeholder="isListening ? (selectedLang === 'sw' ? 'Ongea sasa...' : 'Listening...') : (selectedLang === 'sw' ? 'Ongea, andika au weka picha...' : 'Speak, type or upload file...')"
            class="flex-1 bg-slate-800 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs focus:outline-none focus:border-indigo-500 placeholder-slate-500"
          />

          <button
            type="submit"
            :disabled="(!userInput.trim() && !attachedFile) || loading"
            class="p-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center justify-center"
          >
            <svg class="w-4 h-4 transform rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #334155;
  border-radius: 9999px;
}
</style>
